import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { useForm, usePage } from '@inertiajs/react'
import { useCallback } from 'react'
import type { SharedProps } from '@/types/inertia'
import { IconSelect } from '@/components/ui/IconSelect'
import { CARE_ACTION_ICONS } from '@/lib/icons'

interface CareTaskData {
  id: number
  task_type: string | null
  frequency_days: number | null
  instructions: string | null
  is_active: boolean
  next_due_at: string | null
}

interface Props {
  plant: { id: number; name: string }
  care_task: CareTaskData
}

const taskTypeOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'watering', label: 'Podlewanie' },
  { value: 'fertilizing', label: 'Nawożenie' },
  { value: 'pruning', label: 'Przycinanie' },
  { value: 'repotting', label: 'Przesadzanie' },
  { value: 'misting', label: 'Zraszanie' },
  { value: 'rotating', label: 'Obracanie' },
  { value: 'cleaning', label: 'Czyszczenie' },
  { value: 'pest_check', label: 'Kontrola szkodników' },
]

function CareTasksEdit({ plant, care_task }: Props) {
  const { errors } = usePage<SharedProps>().props

  const { data, setData, put, processing } = useForm({
    care_task: {
      task_type: care_task.task_type || '',
      frequency_days: care_task.frequency_days || '',
      instructions: care_task.instructions || '',
      is_active: care_task.is_active ?? true,
      next_due_at: care_task.next_due_at ? care_task.next_due_at.slice(0, 10) : '',
    },
  })

  const setField = useCallback((field: string, value: unknown) => {
    setData('care_task', { ...data.care_task, [field]: value })
  }, [data.care_task, setData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    put(`/care_tasks/${care_task.id}`)
  }

  return (
    <AtlasPage>
      <PageTitle title="Edycja zadania" subtitle={plant.name} />
      <Separator />

      <form onSubmit={handleSubmit} className="space-y-md">
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <FormField label="Typ zadania" error={errors?.task_type}>
            <IconSelect value={data.care_task.task_type} onChange={(v) => setField('task_type', v)} options={taskTypeOptions} iconMap={CARE_ACTION_ICONS} />
          </FormField>

          <FormField label="Częstotliwość (dni)" error={errors?.frequency_days}>
            <input
              type="number"
              value={data.care_task.frequency_days}
              onChange={(e) => setField('frequency_days', e.target.value)}
              className="form-input"
              min="1"
            />
          </FormField>

          <FormField label="Następne wykonanie">
            <input
              type="date"
              value={data.care_task.next_due_at}
              onChange={(e) => setField('next_due_at', e.target.value)}
              className="form-input"
            />
          </FormField>

          <FormField label="Aktywne">
            <label className="inline-flex items-center gap-sm mt-xs">
              <input
                type="checkbox"
                checked={data.care_task.is_active}
                onChange={(e) => setField('is_active', e.target.checked)}
                className="h-4 w-4 border-frame text-botanical focus:ring-botanical"
              />
              <span className="font-body text-sm text-ink">Zadanie jest aktywne</span>
            </label>
          </FormField>
        </div>

        <FormField label="Instrukcje">
          <textarea
            value={data.care_task.instructions}
            onChange={(e) => setField('instructions', e.target.value)}
            className="form-input min-h-[4rem]"
            placeholder="Szczegółowe instrukcje wykonania..."
          />
        </FormField>

        <Separator />

        <div className="flex items-center justify-center gap-md">
          <Button type="submit" disabled={processing}>
            Zapisz zmiany
          </Button>
          <Button href={`/plants/${plant.id}`} variant="secondary">
            Anuluj
          </Button>
        </div>
      </form>

      <PageNumber number={16} />
    </AtlasPage>
  )
}

function FormField({
  label,
  error,
  children,
}: {
  label: string
  error?: string[]
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">
        {label}
      </label>
      {children}
      {error && (
        <p className="text-xs text-danger mt-xs">{Array.isArray(error) ? error.join(', ') : error}</p>
      )}
    </div>
  )
}

CareTasksEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default CareTasksEdit
